import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["source", "target", "status", "button"]

  static values = {
    endpoint: { type: String, default: "/translations" },
    sourceLanguage: String,
    targetLanguage: { type: String, default: "EN" },
    format: { type: String, default: "text" }
  }

  async translate(event) {
    event.preventDefault()

    const source = this.elementFor(event.params.source, this.hasSourceTarget ? this.sourceTarget : null)
    const target = this.elementFor(event.params.target, this.hasTargetTarget ? this.targetTarget : null)
    const status = this.elementFor(event.params.status, this.hasStatusTarget ? this.statusTarget : null)
    const format = this.normalizedFormat(event.params.format)
    if (!source || !target) return

    const text = this.readContent(source, format)
    if (!text.trim()) return

    const button = event.currentTarget || (this.hasButtonTarget ? this.buttonTarget : null)
    const wasDisabled = button && button.disabled
    if (button) button.disabled = true
    this.clearStatus(status)

    try {
      const response = await fetch(this.endpointValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(this.requestBody(text, format))
      })

      const payload = await this.readResponse(response)
      if (!response.ok || typeof payload.translated_text !== "string") {
        throw new Error(payload.error || "Die Übersetzung konnte nicht erstellt werden.")
      }

      this.writeContent(target, payload.translated_text, format)
      this.dispatchContentEvents(target)
    } catch (error) {
      this.showError(error.message || "Die Übersetzung konnte nicht erstellt werden.", status)
    } finally {
      if (button) button.disabled = wasDisabled
    }
  }

  requestBody(text, format = this.normalizedFormat()) {
    const body = {
      text: text,
      target_language: this.targetLanguageValue || "EN",
      format: format
    }

    if (this.hasSourceLanguageValue && this.sourceLanguageValue.trim()) {
      body.source_language = this.sourceLanguageValue
    }

    return body
  }

  readContent(element, format = this.normalizedFormat()) {
    if (this.isTrixEditor(element)) return element.value
    if (this.isFormControl(element)) return element.value
    return format === "html" ? element.innerHTML : element.textContent
  }

  writeContent(element, content, format = this.normalizedFormat()) {
    if (this.isTrixEditor(element)) {
      element.value = content
    } else if (this.isFormControl(element)) {
      element.value = content
    } else if (format === "html") {
      element.innerHTML = content
    } else {
      element.textContent = content
    }
  }

  isFormControl(element) {
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
  }

  isTrixEditor(element) {
    return element.tagName === "TRIX-EDITOR"
  }

  normalizedFormat(format = this.formatValue) {
    return format && format.toLowerCase() === "html" ? "html" : "text"
  }

  elementFor(selector, fallback) {
    return selector ? this.element.querySelector(selector) : fallback
  }

  dispatchContentEvents(element) {
    element.dispatchEvent(new Event("input", { bubbles: true }))
    element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.content : ""
  }

  async readResponse(response) {
    try {
      return await response.json()
    } catch (_error) {
      throw new Error("Die Übersetzung konnte nicht erstellt werden.")
    }
  }

  clearStatus(status = this.hasStatusTarget ? this.statusTarget : null) {
    if (status) status.textContent = ""
  }

  showError(message, status = this.hasStatusTarget ? this.statusTarget : null) {
    if (status) status.textContent = message
  }
}
